<script lang="ts" setup>
import type { StrapiPageLocalizationData } from '@autobid/strapi-integration/typescript/strapi/Page'
import type {
  LanguageSwitcherLocale,
  LanguageSwitcherType,
} from '@autobid/ui/types/components/LanguageSwitcher'
import type { Locale } from '@intlify/core-base'
import { removeDynamicPartOfSlug } from '@autobid/strapi-integration/utils/helpers'
import { computed, inject } from 'vue'

interface Props {
  locales?: StrapiPageLocalizationData[]
  x: 'left' | 'right'
  y: 'top' | 'bottom'
}

const props = withDefaults(defineProps<Props>(), {
  locales: () => [],
})

const type = inject<LanguageSwitcherType>('languageSwitcherType', 'dropdown')

const { fullPath, name } = useRoute()
const splittedPath = fullPath.split('/')
const isItStrapiPage = String(name).startsWith('slugs___')
const switchLocalePath = !isItStrapiPage ? useSwitchLocalePath() : null

const customI18nLinkFunction = inject<undefined | ((locale: string) => string | null)>('customI18nLinkFunction', undefined)

const parsedLocales = computed((): LanguageSwitcherLocale[] => {
  return props.locales
    .map((locale) => {
      const langCode = String(locale.locale)
      const queryParams = fullPath.split('?')[1] || ''

      if (customI18nLinkFunction) {
        const customLink = customI18nLinkFunction(langCode)

        if (!customLink) {
          return null
        }

        return {
          locale: langCode,
          link: `${customLink}?${queryParams}`,
        }
      }
      if (switchLocalePath) {
        return {
          locale: langCode,
          link: switchLocalePath(langCode as Locale),
        }
      }

      let result = `/${langCode}/`
      // the "slug" word is dynamic part of url, look at the function "getSlugFromUrl"
      const propsSlug = removeDynamicPartOfSlug(locale.slug)

      if (!propsSlug) {
        result += splittedPath.slice(2).join('/')
      }
      else if (propsSlug !== '/') {
        result += `${propsSlug}/${splittedPath.slice(3).join('/')}`
      }

      return {
        locale: langCode,
        link: `${result}?${queryParams}`,
      }
    })
    .filter(el => el !== null)
})
</script>

<template>
  <template v-if="parsedLocales.length">
    <LazyLayoutLanguageSwitcherTypeDropdown
      v-if="type === 'dropdown'"
      :locales="parsedLocales"
      :x="x"
      :y="y"
    />

    <LazyLayoutLanguageSwitcherTypeInline
      v-else-if="type === 'inline'"
      :locales="parsedLocales"
      :x="x"
      :y="y"
    />
  </template>
</template>
